import React, { FC, useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import {
  Button,
  Box,
  Typography,
  Stack,
  Popover,
  Grid,
  TextField,
  InputAdornment,
} from '@mui/material';
import {
  Palette as PaletteIcon,
  Check as CheckIcon,
} from '@mui/icons-material';

interface QRCodeColorPickerProps {
  backgroundColor: string | null;
  foregroundColor: string;
  onBackgroundColorSelect: (color: string | null) => void;
  onForegroundColorSelect: (color: string) => void;
}

// Предустановленные цвета
const presetColors = [
  '#FFFFFF', // Белый
  '#000000', // Черный
  '#F5F5F5', // Светло-серый
  '#E0E0E0', // Серый
  '#FFEB3B', // Желтый
  '#FF9800', // Оранжевый
  '#F44336', // Красный
  '#E91E63', // Розовый
  '#9C27B0', // Фиолетовый
  '#673AB7', // Темно-фиолетовый
  '#3F51B5', // Синий
  '#2196F3', // Голубой
  '#00BCD4', // Циан
  '#009688', // Бирюзовый
  '#4CAF50', // Зеленый
  '#8BC34A', // Светло-зеленый
];

const QRCodeColorPicker: FC<QRCodeColorPickerProps> = ({ 
  backgroundColor, 
  foregroundColor,
  onBackgroundColorSelect,
  onForegroundColorSelect 
}) => {
  const { t } = useTranslation();
  const [backgroundPickerAnchor, setBackgroundPickerAnchor] = useState<HTMLButtonElement | null>(null);
  const [foregroundPickerAnchor, setForegroundPickerAnchor] = useState<HTMLButtonElement | null>(null);
  const [backgroundHexInput, setBackgroundHexInput] = useState<string>(backgroundColor || '#FFFFFF');
  const [foregroundHexInput, setForegroundHexInput] = useState<string>(foregroundColor);
  const [backgroundColorPicker, setBackgroundColorPicker] = useState<string>(backgroundColor || '#FFFFFF');
  const [foregroundColorPicker, setForegroundColorPicker] = useState<string>(foregroundColor);

  // Валидация hex-кода
  const isValidHex = (hex: string): boolean => {
    return /^#([A-Fa-f0-9]{6}|[A-Fa-f0-9]{3})$/.test(hex);
  };

  const handleBackgroundColorSelect = (color: string | null) => {
    onBackgroundColorSelect(color);
    if (color) {
      setBackgroundHexInput(color);
      setBackgroundColorPicker(color);
    }
    setBackgroundPickerAnchor(null);
  };

  const handleForegroundColorSelect = (color: string) => {
    onForegroundColorSelect(color);
    setForegroundHexInput(color);
    setForegroundColorPicker(color);
    setForegroundPickerAnchor(null);
  };

  const handleBackgroundHexChange = (value: string) => {
    setBackgroundHexInput(value);
    if (isValidHex(value)) {
      setBackgroundColorPicker(value);
      onBackgroundColorSelect(value);
    }
  };

  const handleForegroundHexChange = (value: string) => {
    setForegroundHexInput(value);
    if (isValidHex(value)) {
      setForegroundColorPicker(value);
      onForegroundColorSelect(value);
    }
  };

  const handleBackgroundColorPickerChange = (value: string) => {
    setBackgroundColorPicker(value);
    setBackgroundHexInput(value);
    onBackgroundColorSelect(value);
  };

  const handleForegroundColorPickerChange = (value: string) => {
    setForegroundColorPicker(value);
    setForegroundHexInput(value);
    onForegroundColorSelect(value);
  };

  // Синхронизация состояний при изменении пропсов
  useEffect(() => {
    if (backgroundColor) {
      setBackgroundHexInput(backgroundColor);
      setBackgroundColorPicker(backgroundColor);
    }
  }, [backgroundColor]);

  useEffect(() => {
    setForegroundHexInput(foregroundColor);
    setForegroundColorPicker(foregroundColor);
  }, [foregroundColor]);

  return (
    <>
      <Stack direction="row" spacing={1} sx={{ justifyContent: 'center', flexWrap: 'wrap', gap: 1 }}>
        <Button
          variant={backgroundColor === null ? 'contained' : 'outlined'}
          onClick={() => handleBackgroundColorSelect(null)}
          sx={{
            minWidth: '140px',
            backgroundColor: backgroundColor === null ? '#1976d2' : 'transparent',
            border: backgroundColor === null ? 'none' : '1px solid #1976d2',
            '&:hover': {
              backgroundColor: backgroundColor === null ? '#1565c0' : 'rgba(25, 118, 210, 0.04)',
            },
          }}
        >
          {t('qrCode.transparentBackground', 'Прозрачный фон')}
        </Button>
        <Button
          variant="outlined"
          startIcon={<PaletteIcon />}
          onClick={(e) => setBackgroundPickerAnchor(e.currentTarget)}
          sx={{
            minWidth: '140px',
          }}
        >
          {t('qrCode.backgroundColor', 'Цвет фона')}
        </Button>
        <Button
          variant="outlined"
          startIcon={<PaletteIcon />}
          onClick={(e) => setForegroundPickerAnchor(e.currentTarget)}
          sx={{
            minWidth: '140px',
            borderColor: foregroundColor,
            color: foregroundColor,
            '&:hover': {
              borderColor: foregroundColor,
              backgroundColor: `${foregroundColor}15`,
            },
          }}
        >
          {t('qrCode.foregroundColor', 'Цвет QR-кода')}
        </Button>
      </Stack>

      {/* Popover с выбором цвета фона */}
      <Popover
        open={Boolean(backgroundPickerAnchor)}
        anchorEl={backgroundPickerAnchor}
        onClose={() => setBackgroundPickerAnchor(null)}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'center',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'center',
        }}
      >
        <Box sx={{ p: 2, minWidth: '320px' }}>
          <Typography variant="subtitle2" sx={{ mb: 2 }}>
            {t('qrCode.selectBackgroundColor', 'Выберите цвет фона:')}
          </Typography>
          
          {/* Color picker и hex input */}
          <Stack spacing={2} sx={{ mb: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <input
                type="color"
                value={backgroundColorPicker}
                onChange={(e) => handleBackgroundColorPickerChange(e.target.value)}
                style={{
                  width: '60px',
                  height: '40px',
                  border: '1px solid #ddd',
                  borderRadius: '4px',
                  cursor: 'pointer',
                }}
              />
              <TextField
                size="small"
                fullWidth
                value={backgroundHexInput}
                onChange={(e) => handleBackgroundHexChange(e.target.value)}
                placeholder="#FFFFFF"
                error={!isValidHex(backgroundHexInput) && backgroundHexInput !== ''}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Box
                        sx={{
                          width: 20,
                          height: 20,
                          borderRadius: 1,
                          backgroundColor: isValidHex(backgroundHexInput) ? backgroundHexInput : '#FFFFFF',
                          border: '1px solid #ddd',
                        }}
                      />
                    </InputAdornment>
                  ),
                }}
                helperText={!isValidHex(backgroundHexInput) && backgroundHexInput !== '' ? t('qrCode.invalidHex', 'Неверный формат hex') : ''}
              />
            </Box>
          </Stack>

          <Typography variant="caption" sx={{ mb: 1, display: 'block', color: 'text.secondary' }}>
            {t('qrCode.presetColors', 'Готовые цвета:')}
          </Typography>
          <Grid container spacing={1}>
            {presetColors.map((color) => (
              <Grid item xs={3} key={color}>
                <Box
                  onClick={() => handleBackgroundColorSelect(color)}
                  sx={{
                    width: '100%',
                    aspectRatio: '1',
                    backgroundColor: color,
                    border: backgroundColor === color ? '3px solid #1976d2' : '1px solid #ddd',
                    borderRadius: 1,
                    cursor: 'pointer',
                    position: 'relative',
                    '&:hover': {
                      border: '2px solid #1976d2',
                      transform: 'scale(1.1)',
                    },
                    transition: 'all 0.2s',
                  }}
                >
                  {backgroundColor === color && (
                    <CheckIcon
                      sx={{
                        position: 'absolute',
                        top: '50%',
                        left: '50%',
                        transform: 'translate(-50%, -50%)',
                        color: '#fff',
                        fontSize: '1.2rem',
                        filter: 'drop-shadow(0 0 2px rgba(0,0,0,0.5))',
                      }}
                    />
                  )}
                </Box>
              </Grid>
            ))}
          </Grid>
        </Box>
      </Popover>

      {/* Popover с выбором цвета QR-кода */}
      <Popover
        open={Boolean(foregroundPickerAnchor)}
        anchorEl={foregroundPickerAnchor}
        onClose={() => setForegroundPickerAnchor(null)}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'center',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'center',
        }}
      >
        <Box sx={{ p: 2, minWidth: '320px' }}>
          <Typography variant="subtitle2" sx={{ mb: 2 }}>
            {t('qrCode.selectForegroundColor', 'Выберите цвет QR-кода:')}
          </Typography>
          
          {/* Color picker и hex input */}
          <Stack spacing={2} sx={{ mb: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <input
                type="color"
                value={foregroundColorPicker}
                onChange={(e) => handleForegroundColorPickerChange(e.target.value)}
                style={{
                  width: '60px',
                  height: '40px',
                  border: '1px solid #ddd',
                  borderRadius: '4px',
                  cursor: 'pointer',
                }}
              />
              <TextField
                size="small"
                fullWidth
                value={foregroundHexInput}
                onChange={(e) => handleForegroundHexChange(e.target.value)}
                placeholder="#000000"
                error={!isValidHex(foregroundHexInput) && foregroundHexInput !== ''}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Box
                        sx={{
                          width: 20,
                          height: 20,
                          borderRadius: 1,
                          backgroundColor: isValidHex(foregroundHexInput) ? foregroundHexInput : '#000000',
                          border: '1px solid #ddd',
                        }}
                      />
                    </InputAdornment>
                  ),
                }}
                helperText={!isValidHex(foregroundHexInput) && foregroundHexInput !== '' ? t('qrCode.invalidHex', 'Неверный формат hex') : ''}
              />
            </Box>
          </Stack>

          <Typography variant="caption" sx={{ mb: 1, display: 'block', color: 'text.secondary' }}>
            {t('qrCode.presetColors', 'Готовые цвета:')}
          </Typography>
          <Grid container spacing={1}>
            {presetColors.map((color) => (
              <Grid item xs={3} key={color}>
                <Box
                  onClick={() => handleForegroundColorSelect(color)}
                  sx={{
                    width: '100%',
                    aspectRatio: '1',
                    backgroundColor: color,
                    border: foregroundColor === color ? '3px solid #1976d2' : '1px solid #ddd',
                    borderRadius: 1,
                    cursor: 'pointer',
                    position: 'relative',
                    '&:hover': {
                      border: '2px solid #1976d2',
                      transform: 'scale(1.1)',
                    },
                    transition: 'all 0.2s',
                  }}
                >
                  {foregroundColor === color && (
                    <CheckIcon
                      sx={{
                        position: 'absolute',
                        top: '50%',
                        left: '50%',
                        transform: 'translate(-50%, -50%)',
                        color: '#fff',
                        fontSize: '1.2rem',
                        filter: 'drop-shadow(0 0 2px rgba(0,0,0,0.5))',
                      }}
                    />
                  )}
                </Box>
              </Grid>
            ))}
          </Grid>
        </Box>
      </Popover>
    </>
  );
};

export default QRCodeColorPicker;


